import type { FC } from "react";

import { BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF } from "@arti/ai-models-config/bedrockAnthropic";
import { BEDROCK_CONVERSE_OPUS_5_5_MODEL_REF } from "@arti/ai-models-config/claudeOpus";

import { InstanceAvatar } from "../blackboard/InstanceAvatar";
import { FEATURED_MODELS } from "./landingModels";
import {
  ArrowRightIcon,
  CheckIcon,
  Chip,
  Eyebrow,
  MONO_LABEL_CLASS_NAME,
  SubduedText,
  LandingSection,
  LiveDot,
  ModelBadge,
  Panel,
  PRIMARY_BUTTON_CLASS_NAME,
  SECONDARY_BUTTON_CLASS_NAME,
  SectionHeading,
  WindowFrame,
} from "./landingPrimitives";
import { WaitlistCard } from "./WaitlistCard";

interface LifecycleStep {
  body: string;
  eyebrow: string;
  title: string;
}

/** Closed set, so a renamed kicker cannot silently lose its vignette. */
type CapabilityKicker =
  | "Channel-native collaboration"
  | "Durable memory"
  | "Glass-box timeline"
  | "Park and wake"
  | "Persistent identity"
  | "Sandboxed workspaces";

interface CapabilityCardData {
  body: string;
  kicker: CapabilityKicker;
  title: string;
}

interface DifferentiatorCardData {
  bullets: readonly string[];
  highlight: boolean;
  title: string;
}

type TimelineActorId = "nyx" | "sage" | "user";

interface TimelineActor {
  accentBg: string;
  accentBorder: string;
  accentText: string;
  initial: string;
  name: string;
  role: string;
  seed?: string;
  modelRef?: string;
}

type TimelineEventTone = "amber" | "emerald" | "slate" | "violet";

type TimelineRow =
  | { actor: TimelineActorId; body: string; kind: "message"; time: string }
  | { kind: "event"; label: string; time: string; tone: TimelineEventTone };

/* ------------------------------------------------------------------ */
/*  Content data                                                       */
/* ------------------------------------------------------------------ */

const LIFECYCLE_STEPS: readonly LifecycleStep[] = [
  {
    body: "Pick a template, give it a name, drop it into a channel. Its identity, model and tools are set from day one, and it can start work straight away.",
    eyebrow: "01 · Spawn",
    title: "Spawn it from a template.",
  },
  {
    body: "@mention it like any colleague. It wakes, pulls what it remembers, works in its own sandboxed environment, and reports back on the channel timeline, where everyone can see.",
    eyebrow: "02 · Delegate",
    title: "Talk to it like a colleague.",
  },
  {
    body: "Work done, it parks itself. Parked actors cost nothing and wake on the next @mention, scheduled tick, or Jira event with every decision and lesson intact.",
    eyebrow: "03 · Park",
    title: "Idle costs zero.",
  },
] as const;

const CAPABILITY_CARDS: readonly CapabilityCardData[] = [
  {
    body: "Two memory layers: episodic observations and consolidated semantic knowledge. Actors compact their own history and carry it across restarts, model upgrades, and months of work.",
    kicker: "Durable memory",
    title: "Remembers like a teammate.",
  },
  {
    body: "An actor is an individual: a name, a role, an identity prompt, an avatar. You @mention Sage, not “run #3481”, and Sage answers with the context of everything Sage has done.",
    kicker: "Persistent identity",
    title: "A colleague, not a job ID.",
  },
  {
    body: "Actors park between tasks and wake on @mentions, mailbox events, or their own schedule. You pay for work delivered, not for idle compute humming in the background.",
    kicker: "Park and wake",
    title: "Zero burn while parked.",
  },
  {
    body: "Every channel gets an isolated environment: repositories mounted, processes running, ports exposed as preview URLs. Actors build and test in real infrastructure, not a text box.",
    kicker: "Sandboxed workspaces",
    title: "Its own dev environment.",
  },
  {
    body: "Actors share channels with humans and with each other. They @mention a peer for review, hand off work and post shared artifacts. Teams, Jira, and GitHub events route straight in.",
    kicker: "Channel-native collaboration",
    title: "Works where your team talks.",
  },
  {
    body: "Messages and activity merge into one channel timeline, so every wake, tool call, memory write and decision can be inspected as it happens.",
    kicker: "Glass-box timeline",
    title: "Every wake, on the record.",
  },
] as const;

const DIFFERENTIATOR_CARDS: readonly DifferentiatorCardData[] = [
  {
    bullets: [
      "Session ends, context evaporates",
      "You re-explain the codebase every morning",
      "One developer, one cursor, one file at a time",
      "No memory, no identity, no accountability",
    ],
    highlight: false,
    title: "Copilots",
  },
  {
    bullets: [
      "Spun up per task, gone after the PR",
      "No memory of last week's decisions",
      "Can't be @mentioned, can't collaborate",
      "You babysit every single run",
    ],
    highlight: false,
    title: "Single-run agents",
  },
  {
    bullets: [
      "Persistent identity with durable memory",
      "Lives in your channels, wakes on @mentions and events",
      "Owns a sandboxed environment with your repos",
      "Parks at $0 and picks up where it left off",
    ],
    highlight: true,
    title: "Arti actors",
  },
] as const;

/* ------------------------------------------------------------------ */
/*  Channel timeline mockup data                                       */
/* ------------------------------------------------------------------ */

const TIMELINE_ACTORS: Record<TimelineActorId, TimelineActor> = {
  // Accent hues track each seed's deterministic DiceBear background
  // (sage → amber robot, nyx → emerald robot) so identity reads
  // consistently between the avatar and the name across all sections.
  nyx: {
    accentBg: "bg-emerald-500/10",
    accentBorder: "border-emerald-500/40",
    accentText: "text-emerald-400",
    initial: "N",
    modelRef: BEDROCK_CONVERSE_OPUS_5_5_MODEL_REF,
    name: "Nyx",
    role: "actor",
    seed: "nyx",
  },
  sage: {
    accentBg: "bg-amber-500/10",
    accentBorder: "border-amber-500/40",
    accentText: "text-amber-400",
    initial: "S",
    modelRef: BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF,
    name: "Sage",
    role: "actor",
    seed: "sage",
  },
  user: {
    accentBg: "bg-sky-500/10",
    accentBorder: "border-sky-500/40",
    accentText: "text-sky-400",
    initial: "A",
    name: "Alan",
    role: "you",
  },
};

const TIMELINE_ROWS: readonly TimelineRow[] = [
  {
    actor: "user",
    body: "@Sage can you take PROJ-214? The auth suite is flaky again.",
    kind: "message",
    time: "09:12",
  },
  {
    kind: "event",
    label: "SAGE WOKE · @MENTION",
    time: "09:12",
    tone: "emerald",
  },
  {
    actor: "sage",
    body: "On it. I remember this suite: the retry helper from March masks a race between token refresh and the clock mock. Reproducing in the sandbox.",
    kind: "message",
    time: "09:13",
  },
  {
    kind: "event",
    label: "SANDBOX · pnpm vitest auth.spec.ts · 1 failing",
    time: "09:14",
    tone: "slate",
  },
  {
    kind: "event",
    label: "MEMORY · refresh fires before the clock mock pins",
    time: "09:15",
    tone: "violet",
  },
  {
    actor: "sage",
    body: "@Nyx you reviewed the token refresh path. Can you check my fix before I open the PR?",
    kind: "message",
    time: "09:16",
  },
  {
    kind: "event",
    label: "NYX WOKE · @MENTION",
    time: "09:16",
    tone: "emerald",
  },
  {
    actor: "nyx",
    body: "Race confirmed. Your fix is right: pin the clock before starting the refresher and the suite goes green.",
    kind: "message",
    time: "09:18",
  },
  {
    kind: "event",
    label: "PR #512 OPENED · PROJ-214 · TESTS PASSING",
    time: "09:21",
    tone: "amber",
  },
  {
    kind: "event",
    label: "SAGE PARKED · IDLE COST $0.00",
    time: "09:22",
    tone: "emerald",
  },
] as const;

const EVENT_TONE_CLASS: Record<TimelineEventTone, string> = {
  amber: "text-amber-400",
  emerald: "text-emerald-400",
  slate: "text-slate-500",
  violet: "text-violet-400",
};

const EVENT_TONE_DOT: Record<TimelineEventTone, string> = {
  amber: "bg-amber-400",
  emerald: "bg-emerald-400",
  slate: "bg-slate-500",
  violet: "bg-violet-400",
};

/* ------------------------------------------------------------------ */
/*  Channel timeline mockup                                            */
/* ------------------------------------------------------------------ */

const TimelineAvatar: FC<{ actor: TimelineActor }> = ({ actor }) => {
  if (actor.seed) {
    return (
      <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center">
        <InstanceAvatar seed={actor.seed} size="sm" />
      </span>
    );
  }
  return (
    <span
      className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center border ${actor.accentBorder} ${actor.accentBg} font-mono text-[11px] font-bold ${actor.accentText}`}
    >
      {actor.initial}
    </span>
  );
};

const TimelineMessageRow: FC<{
  actor: TimelineActor;
  body: string;
  time: string;
}> = ({ actor, body, time }) => (
  <div className="flex items-start gap-3 px-4 py-2.5">
    <TimelineAvatar actor={actor} />
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-2">
        <span
          className={`font-mono text-[10px] font-bold uppercase tracking-widest ${actor.accentText}`}
        >
          {actor.name}
        </span>
        {actor.modelRef ? (
          <ModelBadge modelRef={actor.modelRef} size="sm" />
        ) : (
          <span className="border border-slate-700 px-1.5 py-px font-mono text-[8px] font-bold uppercase tracking-widest text-slate-500">
            {actor.role}
          </span>
        )}
        <span className="ml-auto flex-shrink-0 font-mono text-[10px] tabular-nums text-slate-600">
          {time}
        </span>
      </div>
      <p className="mt-1 text-[13px] leading-6 text-slate-300">{body}</p>
    </div>
  </div>
);

const TimelineEventRow: FC<{
  label: string;
  time: string;
  tone: TimelineEventTone;
}> = ({ label, time, tone }) => (
  <div className="flex items-center gap-2 px-4 py-1.5">
    <span className="w-7 flex-shrink-0" />
    <span
      className={`h-1.5 w-1.5 flex-shrink-0 ${EVENT_TONE_DOT[tone]}`}
      aria-hidden="true"
    />
    <span
      className={`min-w-0 flex-1 truncate font-mono text-[10px] tracking-wide ${EVENT_TONE_CLASS[tone]}`}
    >
      {label}
    </span>
    <span className="flex-shrink-0 font-mono text-[10px] tabular-nums text-slate-600">
      {time}
    </span>
  </div>
);

const ChannelTimelineMockup: FC = () => (
  <WindowFrame
    title="#platform-eng · shared channel"
    status={<LiveDot />}
    corners
    footer={
      <div className="flex items-center justify-between gap-4 font-mono text-[10px] uppercase tabular-nums tracking-widest text-slate-500">
        <span>2 wakes · 1 parked</span>
        <span>
          Session spend <span className="text-emerald-400">$0.41</span>
        </span>
      </div>
    }
  >
    <div className="divide-y divide-slate-800 py-1">
      {TIMELINE_ROWS.map((row) =>
        row.kind === "message" ? (
          <TimelineMessageRow
            key={`${row.time}-${row.actor}`}
            actor={TIMELINE_ACTORS[row.actor]}
            body={row.body}
            time={row.time}
          />
        ) : (
          <TimelineEventRow
            key={`${row.time}-${row.label}`}
            label={row.label}
            time={row.time}
            tone={row.tone}
          />
        ),
      )}
    </div>
  </WindowFrame>
);

/* ------------------------------------------------------------------ */
/*  1. Hero                                                            */
/* ------------------------------------------------------------------ */

const HeroModelChips: FC = () => (
  <div className="mt-8 flex flex-wrap items-center gap-2">
    <span className={`${MONO_LABEL_CLASS_NAME} mr-1 text-slate-500`}>
      Runs on
    </span>
    {FEATURED_MODELS.map((model) => (
      <Chip
        key={model.ref}
        square={model.isNew}
        tone={model.isNew ? "emerald" : "slate"}
      >
        {model.name}
      </Chip>
    ))}
  </div>
);

const HeroCopy: FC = () => (
  <div className="min-w-0">
    <Eyebrow>Spawn once · Keep forever</Eyebrow>
    <h1 className="mt-5 text-balance font-display text-[2.6rem] font-bold normal-case leading-[1.04] !tracking-tight text-white sm:text-6xl">
      AI teammates that remember.
    </h1>
    <p className="mt-6 max-w-xl text-base leading-7 text-slate-400 sm:text-lg sm:leading-8">
      Arti actors are AI colleagues that live in your team&apos;s channels. They
      remember months of work, build and test in their own sandboxed dev
      environments, and park between tasks at no cost.
    </p>
    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
      <a href="#auth" className={PRIMARY_BUTTON_CLASS_NAME}>
        Request access
        <ArrowRightIcon />
      </a>
      <a href="/billing" className={SECONDARY_BUTTON_CLASS_NAME}>
        See pricing
      </a>
    </div>
    <HeroModelChips />
  </div>
);

export const LandingHeroSection: FC = () => (
  <section className="px-4 pb-20 pt-16 sm:px-6 sm:pt-24 lg:pb-28">
    <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
      <HeroCopy />
      <div className="min-w-0">
        <ChannelTimelineMockup />
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  2. Lifecycle                                                       */
/* ------------------------------------------------------------------ */

const SpawnVignette: FC = () => (
  <div className="min-h-[190px] overflow-hidden border border-slate-700 bg-[#0b1222]">
    {/* Header bar */}
    <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
      <span className="font-mono text-[10px] tracking-[0.25em] text-slate-400">
        [ SPAWN ]
      </span>
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
        <span className="h-1.5 w-1.5 bg-emerald-400" aria-hidden="true" />
        NEW
      </span>
    </div>

    {/* Body */}
    <div className="flex min-w-0 items-start gap-3 px-3 py-3">
      <div className="flex-shrink-0">
        <InstanceAvatar seed="sage" size="lg" />
      </div>
      <div className="min-w-0 flex-1 space-y-1 font-mono text-[10px]">
        <div className="truncate text-white">NAME · SAGE</div>
        <div className="truncate text-slate-400">
          TEMPLATE · PLATFORM ENGINEER
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="flex-shrink-0 text-white">MODEL ·</span>
          <ModelBadge
            modelRef={BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF}
            size="sm"
          />
        </div>
        <div className="truncate text-slate-400">CHANNEL · #PLATFORM-ENG</div>
      </div>
    </div>

    {/* Footer */}
    <div className="border-t border-slate-800/60 px-3 py-2">
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-emerald-400">
        <span className="h-1.5 w-1.5 bg-emerald-400" aria-hidden="true" />
        SPAWNED · IDENTITY PINNED
      </div>
    </div>
  </div>
);

const DelegateVignette: FC = () => {
  const userActor = TIMELINE_ACTORS.user;
  const sageActor = TIMELINE_ACTORS.sage;

  return (
    <div className="min-h-[190px] overflow-hidden border border-slate-700 bg-[#0b1222]">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
        <span className="font-mono text-[10px] tracking-[0.25em] text-slate-400">
          [ #PLATFORM-ENG ]
        </span>
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 bg-emerald-400" />
          </span>
          LIVE
        </span>
      </div>

      {/* Body - compact timeline rows */}
      <div className="divide-y divide-slate-800/60 py-1">
        {/* User message */}
        <div className="flex min-w-0 items-start gap-2 px-3 py-1.5">
          <span
            className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center border ${userActor.accentBorder} ${userActor.accentBg} font-mono text-[9px] font-bold ${userActor.accentText}`}
          >
            {userActor.initial}
          </span>
          <span
            className={`font-mono text-[10px] font-bold ${userActor.accentText}`}
          >
            {userActor.name}
          </span>
          <span className="min-w-0 flex-1 truncate text-[11px] text-slate-300">
            @Sage take PROJ-214
          </span>
        </div>

        {/* Wake event */}
        <div className="flex min-w-0 items-center gap-2 px-3 py-1.5">
          <span className="w-5 flex-shrink-0" />
          <span
            className="h-1.5 w-1.5 flex-shrink-0 bg-emerald-400"
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-emerald-400">
            SAGE WOKE · MAILBOX: @MENTION
          </span>
        </div>

        {/* Sage response */}
        <div className="flex min-w-0 items-start gap-2 px-3 py-1.5">
          <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center">
            <InstanceAvatar seed="sage" size="xs" />
          </span>
          <span
            className={`font-mono text-[10px] font-bold ${sageActor.accentText}`}
          >
            {sageActor.name}
          </span>
          <span className="min-w-0 flex-1 truncate text-[11px] text-slate-300">
            On it, reproducing in the sandbox.
          </span>
        </div>

        {/* Sandbox event */}
        <div className="flex min-w-0 items-center gap-2 px-3 py-1.5">
          <span className="w-5 flex-shrink-0" />
          <span
            className="h-1.5 w-1.5 flex-shrink-0 bg-slate-500"
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-slate-500">
            SANDBOX · vitest — running
          </span>
        </div>
      </div>
    </div>
  );
};

const SleepVignette: FC = () => (
  <div className="min-h-[190px] overflow-hidden border border-slate-700 bg-[#0b1222]">
    {/* Header bar */}
    <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
      <span className="font-mono text-[10px] tracking-[0.25em] text-slate-400">
        [ PARKED ]
      </span>
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
        <span className="h-1.5 w-1.5 bg-slate-500" aria-hidden="true" />
        IDLE
      </span>
    </div>

    {/* Body */}
    <div className="flex min-w-0 items-start gap-3 px-3 py-3">
      <div className="flex-shrink-0 opacity-40 saturate-50">
        <InstanceAvatar seed="sage" size="lg" />
      </div>
      <div className="min-w-0 flex-1 space-y-1 font-mono text-[10px] tabular-nums">
        <div className="flex flex-wrap items-baseline gap-1.5">
          <span className="text-white">IDLE COST ·</span>
          <span className="text-emerald-400">$0.00</span>
        </div>
        <div className="text-violet-400">MEMORY · CONSOLIDATED</div>
        <div className="text-slate-400">
          WAKES ON · @MENTION / SCHEDULE / JIRA
        </div>
      </div>
    </div>

    {/* Footer */}
    <div className="border-t border-slate-800/60 px-3 py-2">
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-slate-500">
        <span className="h-1.5 w-1.5 bg-slate-500" aria-hidden="true" />
        SLEEPING · EVERYTHING RETAINED
      </div>
    </div>
  </div>
);

const LifecycleFlowConnector: FC = () => (
  <div className="mb-6 hidden items-center lg:flex">
    <div className="flex flex-1 items-center gap-2">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-emerald-400/60 bg-emerald-400/10 font-mono text-[11px] font-bold text-emerald-300">
        01
      </div>
      <div className="flex-1 border-t border-dashed border-slate-700" />
    </div>
    <div className="flex flex-1 items-center gap-2">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-slate-700 font-mono text-[11px] font-bold text-slate-400">
        02
      </div>
      <div className="flex-1 border-t border-dashed border-slate-700" />
    </div>
    <div className="flex flex-1 items-center gap-2">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-slate-700 font-mono text-[11px] font-bold text-slate-400">
        03
      </div>
      <div className="flex-1 border-t border-dashed border-slate-700" />
      <span className="font-mono text-[9px] text-slate-500">↺ WAKES AGAIN</span>
    </div>
  </div>
);

const LifecycleCardWithVignette: FC<{
  vignette: React.ReactNode;
  step: LifecycleStep;
}> = ({ vignette, step }) => (
  <Panel as="article">
    <div className="p-6">
      <div className="mb-6">{vignette}</div>
      <p className={`${MONO_LABEL_CLASS_NAME} text-emerald-400`}>
        {step.eyebrow}
      </p>
      <h3 className="mt-3 font-display text-2xl font-bold normal-case !tracking-tight text-white">
        {step.title}
      </h3>
      <p className="mt-3 text-sm leading-7 text-slate-400">{step.body}</p>
    </div>
  </Panel>
);

export const LandingLifecycleSection: FC = () => (
  <LandingSection id="lifecycle">
    <SectionHeading
      eyebrow="How it works"
      title="Set it up once, then just @mention it."
      body="An actor stays on the team between tasks. When someone needs it, it wakes up, does the work, reports back in the channel and parks again."
    />
    <div className="mt-12">
      <LifecycleFlowConnector />
      <div className="grid gap-4 lg:grid-cols-3">
        <LifecycleCardWithVignette
          vignette={<SpawnVignette />}
          step={LIFECYCLE_STEPS[0]}
        />
        <LifecycleCardWithVignette
          vignette={<DelegateVignette />}
          step={LIFECYCLE_STEPS[1]}
        />
        <LifecycleCardWithVignette
          vignette={<SleepVignette />}
          step={LIFECYCLE_STEPS[2]}
        />
      </div>
    </div>
  </LandingSection>
);

/* ------------------------------------------------------------------ */
/*  3. Capabilities                                                    */
/* ------------------------------------------------------------------ */

const CapabilityCard: FC<{
  body: string;
  kicker: string;
  title: string;
  vignette: React.ReactNode;
}> = ({ body, kicker, title, vignette }) => (
  <Panel as="article" className="p-6 transition hover:border-slate-600">
    <div className="flex h-[92px] min-h-[92px] flex-col justify-center overflow-hidden border border-slate-700 bg-[#0b1222] px-3 py-2.5 font-mono text-[9.5px] leading-relaxed">
      {vignette}
    </div>
    <p className={`mt-5 ${MONO_LABEL_CLASS_NAME} text-emerald-400`}>{kicker}</p>
    <h3 className="mt-3 font-display text-xl font-bold normal-case !tracking-tight text-white">
      {title}
    </h3>
    <p className="mt-3 text-sm leading-7 text-slate-400">{body}</p>
  </Panel>
);

const CAPABILITY_VIGNETTES: Record<CapabilityKicker, React.ReactNode> = {
  "Channel-native collaboration": (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <span className="inline-flex h-6 w-6 items-center justify-center border border-sky-500/40 bg-sky-500/10 font-mono text-[9px] font-bold text-sky-400">
          A
        </span>
        <InstanceAvatar seed="sage" size="sm" />
        <InstanceAvatar seed="nyx" size="sm" />
        <InstanceAvatar seed="juno" size="sm" />
      </div>
      <div className="text-slate-500">
        #PLATFORM-ENG &middot; 1 HUMAN &middot; 3 ACTORS
      </div>
      <div className="text-slate-600">
        TEAMS &middot; JIRA &middot; GITHUB → ROUTED IN
      </div>
    </div>
  ),
  "Durable memory": (
    <div className="space-y-0.5">
      <div className="text-slate-400">EPISODIC &middot; 1,482 OBSERVATIONS</div>
      <div className="text-violet-400">&#x2193; CONSOLIDATE</div>
      <div className="text-slate-300">SEMANTIC &middot; 37 DURABLE FACTS</div>
      <div className="text-right text-[8px] text-slate-600">
        RETAINED ACROSS 4 MODEL UPGRADES
      </div>
    </div>
  ),
  "Glass-box timeline": (
    <div className="space-y-1 tabular-nums">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 bg-emerald-500" />
          <span className="text-slate-300">WAKE &middot; @MENTION</span>
        </div>
        <span className="text-[8px] text-slate-600">09:12</span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 bg-violet-400" />
          <span className="truncate text-slate-300">
            MEMORY WRITE &middot; auth race...
          </span>
        </div>
        <span className="text-[8px] text-slate-600">09:15</span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 bg-amber-400" />
          <span className="text-slate-300">PR #512 OPENED</span>
        </div>
        <span className="text-[8px] text-slate-600">09:21</span>
      </div>
    </div>
  ),
  "Park and wake": (
    <div className="space-y-2">
      <div className="flex items-end gap-px">
        {[
          { h: "h-3", on: true },
          { h: "h-4", on: true },
          { h: "h-3", on: true },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-5", on: true },
          { h: "h-4", on: true },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-1", on: false },
          { h: "h-3", on: true },
          { h: "h-4", on: true },
        ].map((bar, i) => (
          <div
            key={i}
            className={`w-1.5 ${bar.h} ${bar.on ? "bg-emerald-500" : "bg-slate-700"}`}
          />
        ))}
      </div>
      <div className="flex gap-3 text-[9px]">
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 bg-emerald-500" />
          <span className="text-slate-400">WORKING $0.38</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 bg-slate-700" />
          <span className="text-slate-400">PARKED $0.00</span>
        </div>
      </div>
    </div>
  ),
  "Persistent identity": (
    <div className="flex items-end justify-between">
      <div className="flex flex-col items-center gap-1">
        <InstanceAvatar seed="sage" size="sm" />
        <span className="text-slate-500">DAY 1</span>
      </div>
      <div className="flex items-center text-slate-600">— SAME SEED →</div>
      <div className="flex flex-col items-center gap-1">
        <InstanceAvatar seed="sage" size="sm" />
        <span className="text-emerald-400">DAY 214</span>
      </div>
    </div>
  ),
  "Sandboxed workspaces": (
    <div className="space-y-1">
      <div className="text-slate-400">
        repo &middot; agent-arti &middot; main
      </div>
      <div className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 bg-emerald-500" />
        <span className="text-slate-300">
          proc &middot; vite :5173 &middot; RUNNING
        </span>
      </div>
      <div className="text-sky-400">
        :5173 → preview.arti.dev/... &middot; LIVE
      </div>
    </div>
  ),
};

export const LandingCapabilitiesSection: FC = () => (
  <LandingSection id="capabilities">
    <SectionHeading
      eyebrow="Capabilities"
      title="What every actor comes with."
      body="Each actor keeps its own identity, memory and workspace, and has a permanent place in your team's channels."
    />
    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {CAPABILITY_CARDS.map((card) => (
        <CapabilityCard
          key={card.title}
          body={card.body}
          kicker={card.kicker}
          title={card.title}
          vignette={CAPABILITY_VIGNETTES[card.kicker]}
        />
      ))}
    </div>
  </LandingSection>
);

/* ------------------------------------------------------------------ */
/*  4. Differentiators                                                 */
/* ------------------------------------------------------------------ */

const DifferentiatorBulletX: FC = () => (
  <svg
    className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-600"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 6l8 8M14 6l-8 8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const DifferentiatorCard: FC<{
  bullets: readonly string[];
  highlight: boolean;
  title: string;
  statusStrip: React.ReactNode;
}> = ({ bullets, highlight, title, statusStrip }) => (
  <Panel
    as="article"
    className={`p-6 ${highlight ? "border-emerald-500/60 bg-slate-900/60" : ""}`}
  >
    <div className="overflow-hidden border border-slate-700 bg-[#0b1222] px-3 py-2 font-mono text-[9.5px]">
      {statusStrip}
    </div>
    <h3
      className={`mt-5 font-display text-lg font-bold normal-case !tracking-tight ${highlight ? "text-white" : "text-slate-300"}`}
    >
      {title}
    </h3>
    <ul className="mt-4 space-y-3">
      {bullets.map((bullet) => (
        <li key={bullet} className="flex items-start gap-2.5 text-sm">
          {highlight ? (
            <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
          ) : (
            <DifferentiatorBulletX />
          )}
          <span className={highlight ? "text-slate-200" : "text-slate-500"}>
            {bullet}
          </span>
        </li>
      ))}
    </ul>
  </Panel>
);

const DIFFERENTIATOR_STATUS_STRIPS: Record<string, React.ReactNode> = {
  "Arti actors": (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5">
        <InstanceAvatar seed="sage" size="sm" />
        <span className="text-emerald-400">SAGE &middot; DAY 214</span>
      </div>
      <div className="text-slate-300">
        MEMORY INTACT &middot; PARKED AT $0.00
      </div>
    </div>
  ),
  Copilots: (
    <div className="space-y-0.5">
      <div className="text-slate-500">SESSION #4,072 &middot; ENDED</div>
      <div className="text-rose-400/70">CONTEXT &middot; EVAPORATED</div>
    </div>
  ),
  "Single-run agents": (
    <div className="space-y-0.5">
      <div className="text-slate-500">RUN #3481 &middot; COMPLETE</div>
      <div className="text-rose-400/70">
        WORKER DESTROYED &middot; MEMORY LOST
      </div>
    </div>
  ),
};

export const LandingDifferentiatorsSection: FC = () => (
  <LandingSection id="differentiators">
    <SectionHeading
      eyebrow="Why actors"
      title="What copilots and one-off agents can't do."
      body="Most AI coding tools start from zero every session. Arti actors keep their memory, their identity and their place on the team from one task to the next."
    />
    <div className="mt-12 grid gap-4 lg:grid-cols-3">
      {DIFFERENTIATOR_CARDS.map((card) => (
        <DifferentiatorCard
          key={card.title}
          bullets={card.bullets}
          highlight={card.highlight}
          title={card.title}
          statusStrip={DIFFERENTIATOR_STATUS_STRIPS[card.title]}
        />
      ))}
    </div>
  </LandingSection>
);

/* ------------------------------------------------------------------ */
/*  5. Early access CTA                                                */
/* ------------------------------------------------------------------ */

const CTA_CHECKLIST = [
  "No credit card, no setup",
  "Claude and GPT models, chosen per actor",
  "Skill Mesh for Claude and ChatGPT",
] as const;

const CtaChecklist: FC = () => (
  <ul className="space-y-2 text-sm text-slate-300">
    {CTA_CHECKLIST.map((item) => (
      <li key={item} className="flex items-center gap-2.5">
        <CheckIcon className="h-4 w-4 flex-shrink-0 text-emerald-400" />
        {item}
      </li>
    ))}
  </ul>
);

export const LandingAuthSection: FC = () => (
  <section id="auth" className="scroll-mt-24 px-4 pb-24 pt-8 sm:px-6">
    <Panel className="mx-auto max-w-7xl overflow-hidden">
      <div className="relative grid gap-10 p-6 sm:p-10 lg:grid-cols-2 lg:items-center lg:p-14">
        <div className="max-w-xl space-y-5">
          <div className="flex items-center gap-1.5">
            <InstanceAvatar seed="sage" size="sm" />
            <InstanceAvatar seed="nyx" size="sm" />
            <InstanceAvatar seed="juno" size="sm" />
            <InstanceAvatar seed="vesper" size="sm" />
            <span className={`ml-2 ${MONO_LABEL_CLASS_NAME} text-slate-500`}>
              The roster is waiting
            </span>
          </div>
          <h2 className="text-balance font-display text-3xl font-bold normal-case leading-[1.1] !tracking-tight text-white sm:text-4xl lg:text-5xl">
            Give your team <SubduedText>its first actor.</SubduedText>
          </h2>
          <p className="text-base leading-7 text-slate-400 sm:text-lg sm:leading-8">
            Leave your work email and we&apos;ll get in touch. We&apos;re
            onboarding teams in small batches, and we&apos;re happy to talk
            about Skill Mesh or a custom setup too.
          </p>
          <CtaChecklist />
        </div>
        <WaitlistCard />
      </div>
    </Panel>
  </section>
);
